import { governmentAllocationEntriesKind } from '@/common/constants/governmentAllocationEntriesKind.constant';
import { formatCurrency, formatDateRange, formatDateTime, truncateText } from '@/common/utils/formatters';
import { PaymentModalityType } from '@/features/companies/types/companies.type';
import { GovernmentAllocationDetails } from '@/features/privateCompanies/components/GovernmentAllocationDetails';
import { GovernmentAllocationMonthDetails } from '@/features/privateCompanies/components/GovernmentAllocationMonthDetails';
import { companyGovernmentAllocationColumns } from '@/features/privateCompanies/constants/companyGovernmentAllocationColumns.constant';
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type';
import { ArrowBigRight } from 'lucide-react';
import { createElement, Dispatch, ReactElement, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../common/components/Button';
import { companyGovernmentAllocationStatusStrategy } from '../pages/CompanyGovernmentAllocations/Show/constants/governmentAllocationStatus.constant';
import { Tooltip } from 'react-tooltip';
import { AllocatedAndConsumedDetails, ControlledByType } from '../types/companySummaryOfAllAllocatedAndConsumed.type';

export class CompanyGovernmentAllocationTransformer {
  static timelineTransformer({
    companyGovernmentAllocations,
    companyPaymentModality,
    year,
    setGovernmentAllocationIdToSearchSummaryInfo,
    summaryOfAllAllocatedAndConsumed,
    consumptionControlledBy
  }: {
    companyGovernmentAllocations: CompanyGovernmentAllocationData[]
    companyPaymentModality?: PaymentModalityType
    year: number,
    setGovernmentAllocationIdToSearchSummaryInfo: Dispatch<SetStateAction<string>>,
    summaryOfAllAllocatedAndConsumed?: AllocatedAndConsumedDetails,
    consumptionControlledBy?: ControlledByType
  }) {
    return () => {
      return companyGovernmentAllocations.map((governmentAllocation) => {
        const object: Record<string, ReactElement> = {}

        governmentAllocation.applicable_end_date = governmentAllocation.applicable_end_date.replace(/-/g, '/')
        governmentAllocation.applicable_start_date = governmentAllocation.applicable_start_date.replace(/-/g, '/')

        companyGovernmentAllocationColumns().forEach((column, index) => {
          object[column.value] = createElement(GovernmentAllocationMonthDetails, {
            governmentAllocation,
            year,
            month: index - 1,
          })
        })

        object.governmentAllocation = createElement(
          GovernmentAllocationDetails, 
          {
            governmentAllocation,
            companyPaymentModality,
            setGovernmentAllocationIdToSearchSummaryInfo,
            summaryOfAllAllocatedAndConsumed,
            consumptionControlledBy
           }
        )

        return object
      })
    }
  }

  static tableTransformer(companyGovernmentAllocations: CompanyGovernmentAllocationData[], companyId: string, operationKlassToCheckPermission?: string) {
    return companyGovernmentAllocations.map((companyGovernmentAllocation) => {
      const branchName = companyGovernmentAllocation.branch_display_name
      const tooltipId = `${companyGovernmentAllocation.id}-branch-name-tooltip`

      return {
        ...companyGovernmentAllocation,
        office_number: companyGovernmentAllocation.main_entry.office_number,
        branch_display_name: companyGovernmentAllocation.branch_display_name,
        total_value:
          ['completed', 'active'].includes(companyGovernmentAllocation.main_entry.status)
            ? formatCurrency(Number(companyGovernmentAllocation.total_value) || 0)
            : formatCurrency(Number(companyGovernmentAllocation.main_entry.total_value) || 0),
        status: companyGovernmentAllocationStatusStrategy[companyGovernmentAllocation.status as keyof typeof companyGovernmentAllocationStatusStrategy],
        kind: governmentAllocationEntriesKind[companyGovernmentAllocation.main_entry.kind as keyof typeof governmentAllocationEntriesKind],
        applicable_date: formatDateRange(new Date(`${companyGovernmentAllocation.applicable_start_date} `), new Date(`${companyGovernmentAllocation.applicable_end_date} `)),
        company_branch: (
          <>
            <p data-tooltip-id={tooltipId} className="truncate max-w-[220px] cursor-default">
              {truncateText(branchName, 30)}
            </p>
            <Tooltip id={tooltipId} className="z-50">{branchName}</Tooltip>
          </>
        ),
        created_at: companyGovernmentAllocation.main_entry.created_at && formatDateTime(new Date(companyGovernmentAllocation.main_entry.created_at)),
        actions: (
          <>
            <Link to={!['completed', 'active'].includes(companyGovernmentAllocation.main_entry.status)
              ? `/companies/${companyId}/government-allocations/${companyGovernmentAllocation.main_entry.id}/edit`
              : `/companies/${companyId}/government-allocations/${companyGovernmentAllocation.id}`}>
              <Button className="bg-transparent hover:bg-transparent p-0" operationKlassToCheckPermission={operationKlassToCheckPermission}>
                <ArrowBigRight />
              </Button>
            </Link>
          </>
        ),
      };
    });
  }

  static errorsTransformer() {
    return (errors: Record<string, string[]>) => {
      return Object.entries(errors).map(([key, value]) => {
        if (key.startsWith('product_categories_distributions.')) {
          key = '_base';
        }

        return {
          [key]: value.join(', ')
        };
      });
    };
  }
}
